import React, { useState } from 'react';
import {
    couponActions,
    rootActions, savedCreditCardActions, shippingAddressActions,
    useAppDispatch,
    useAppSelector
} from '../../data/store';
import ProductPriceFormat from '../../../../products/product-price/product-price-format';
import Coupon from '../coupon/coupon';
import { CartSteps } from '../../enums/cart-steps';
import InstantSavings from '../instant-savings/instant-savings';
import StepOverviewItem from './step-overview-item';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import { PaymentTabsEnum } from '../../enums/payment-tabs-enum';
import {
    scrollToPath,
    validateBillingAddress,
    validateCreditCards,
    validateShippingAddress
} from '../../domain/shopping-cart-validations';
import { CreditCardTabTabsEnum } from '../../enums/credit-card-tab';
import { ICartData, IConfirmOrderRequest } from '../../interfaces';
import { confirmOrder } from '../../data/data-service';
import Preloader from '../../../../../ui/svg/common/preloader';
import { getAddressIDs } from '../../domain/shopping-cart-address-provider';
import { handleConfirmOrderResponse } from '../../domain/shopping-cart-confirm-order';
import { TaxIdStatusEnum } from '../../enums/tax-id-status-enum';
import { ADDRESS_NOT_VALID_EVENT, sendCartEvent } from '../../domain/shopping-cart-events-provider';
import { isConfirmationMsgVisible } from '../../domain/free-plugin-provider';
import FreePluginBox from '../free-plugin/free-plugin-box';
import { sendGAEvent } from '../../../../../domain/marketing/gtm-provider';
import FreeWithYourPurchaseOverview from '../free-with-your-purchase/free-with-your-purchase-overview';
import { getUTMFromStorage } from '../../../../../domain/marketing/utm/utm-provider';
import { checkIsSubscription } from '../../domain/shopping-cart-provider';
import SubscriptionOverviewItem from '../subscription/subscription-overview-item';
import ConfirmText from './confirm-text';
import { USA_ID } from '../../../../../domain/addresses/addresses-provider';
import { validateControl } from '../../../../../domain/validation/validation';
import ReferCredit from '../refer-credit/refer-credit';
import ReferCreditRecurring from '../refer-credit/credit-recurring';

const Step3Overview = () => {

    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState('');

    const cartData = useAppSelector(store => store.root.cartData);
    const priceData = cartData.priceData;

    const couponControl = useAppSelector(store => store.coupons.couponControl);

    // used for payment tabs validation
    const selectedPaymentTab = useAppSelector(store => store.root.selectedPaymentTab);
    const selectedCreditCardTab = useAppSelector(store => store.root.selectedCreditCardTab);

    // new and saved credit cards
    const zoozSecureFields = useAppSelector(store => store.newCreditCard.zoozSecureFields);
    const nameOnCardControl = useAppSelector(store => store.newCreditCard.nameOnCardControl);
    const cvvControl = useAppSelector(store => store.savedCreditCard.cvvControl);
    const zoozFieldError = useAppSelector(store => store.newCreditCard.zoozFieldError);
    const savedCreditCardToken = useAppSelector(store => store.savedCreditCard.savedCreditCardToken);
    const networkTransactionID = useAppSelector(store => store.savedCreditCard.networkTransactionID);
    const isRecurringNetworkTransactionID = useAppSelector(store => store.savedCreditCard.isRecurringNetworkTransactionID);
    const rememberMe = useAppSelector(store => store.newCreditCard.rememberMe);

    // billing form controls
    const countries = useAppSelector(store => store.root.countries);
    const billToControl = useAppSelector(store => store.billingAddress.billToControl);
    const billingAddressLine1Control = useAppSelector(store => store.billingAddress.addressLine1Control);
    const billingAddressLine2Control = useAppSelector(store => store.billingAddress.addressLine2Control);
    const billingCityControl = useAppSelector(store => store.billingAddress.cityControl);
    const billingZipCodeControl = useAppSelector(store => store.billingAddress.zipCodeControl);
    const billingCountryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const billingStateIdControl = useAppSelector(store => store.billingAddress.stateIdControl);
    const billingPhoneControl = useAppSelector(store => store.billingAddress.phoneControl);
    const billingPhoneCountryIdControl = useAppSelector(store => store.billingAddress.phoneCountryIdControl);

    // shipping form controls
    const isShippingDifferentFromBilling = useAppSelector(store => store.shippingAddress.isShippingDifferentFromBilling);
    const shipToControl = useAppSelector(store => store.shippingAddress.shipToControl);
    const shippingAddressLine1Control = useAppSelector(store => store.shippingAddress.addressLine1Control);
    const shippingAddressLine2Control = useAppSelector(store => store.shippingAddress.addressLine2Control);
    const shippingCityControl = useAppSelector(store => store.shippingAddress.cityControl);
    const shippingZipCodeControl = useAppSelector(store => store.shippingAddress.zipCodeControl);
    const shippingCountryIdControl = useAppSelector(store => store.shippingAddress.countryIdControl);
    const shippingStateIdControl = useAppSelector(store => store.shippingAddress.stateIdControl);
    const shippingPhoneControl = useAppSelector(store => store.shippingAddress.phoneControl);
    const shippingPhoneCountryIdControl = useAppSelector(store => store.shippingAddress.phoneCountryIdControl);
    const upsShippingCodeControl = useAppSelector(store => store.shippingAddress.upsShippingCodeControl);

    // https://wavesaudio.atlassian.net/browse/DSWEB-6
    const taxIdControl = useAppSelector(store => store.billingAddress.taxIdControl);
    const taxIdSelectedStatusControl = useAppSelector(store => store.billingAddress.taxIdSelectedStatusControl);
    const isUSA = billingCountryIdControl && billingCountryIdControl.value.toString() === USA_ID.toString();

    const isInAppPurchase = useAppSelector(store => store.root.isInAppPurchase);
    const isSubscription = checkIsSubscription(cartData);

    // refer a friend credit
    const isReferCreditChecked = useAppSelector(store => store.root.isReferCreditChecked);
    const referCreditControl = useAppSelector(store => store.root.referCreditControl);
    const isReferRecurringChecked = useAppSelector(store => store.root.isReferRecurringChecked);

    /**
     * return to the first step
     */
    const editCartHandler = () => {
        dispatch(
            rootActions.main({
                cartStep: CartSteps.First,
            })
        );

        sendGAEvent('Edit Cart', 'Step 3');
    };

    /**
     * proceed to checkout
     */
    const proceedHandler = async () => {

        let isValid = true;
        let newCreditCardToken = '';

        // if coupon text box has a value, and coupon was not submitted,
        // show a validation error
        if(couponControl.value.trim()) {
            dispatch(
                couponActions.main({
                    showCouponNavError: true,
                })
            );
            return;
        }

        // if my rewards checkbox is checked, and the value was not submitted,
        // show a validation error
        if(isReferCreditChecked && cartData.priceData.selectedUserCredit <= 0) {
            dispatch(
                rootActions.main({
                    showCreditNavError: true,
                })
            );
            return;
        }

        // if credit is enabled & opened,
        // and the text box has a value that is not submitted
        // ----> show a validation error
        if(cartData.isReferFriendEnabled && // setting in kentico
            isReferCreditChecked && // credit checkbox is checked
            cartData.priceData.selectedUserCredit <= 0 && // credit form is visible (not applied)
            referCreditControl.value.trim()) { // credit has some value
            dispatch(
                rootActions.main({
                    showCreditNavError: true,
                })
            );
            return;
        }

        const isZeroPurchase = cartData.priceData.totalPrice < 1 && !checkIsSubscription(cartData);

        /*
        // validate refer friend credit ------------------------
        if(isReferCreditChecked) {
            const isReferValid = validateReferCredit(referCreditControl, dispatch);
            if(!isReferValid) return;
        }*/

        if(!isZeroPurchase && selectedPaymentTab === PaymentTabsEnum.CreditCard){

            const validationResult = await validateCreditCards(
                selectedCreditCardTab,
                nameOnCardControl,
                dispatch,
                zoozSecureFields,
                zoozFieldError,
                cvvControl,
                networkTransactionID
            );

            newCreditCardToken = validationResult.newCreditCardToken;

            if(!validationResult.isValid){
                isValid = false;
            }

            if(!validationResult.isValid && validationResult.type === 'saved'){
                dispatch(
                    savedCreditCardActions.setControl({
                        controlName: 'cvvControl',
                        control: validateControl(cvvControl),
                    })
                );
                scrollToPath('#cvv-tb');
                return;
            }
        }

        // validate billing address --------------------------
        const billingFormFields: [IFormControl, string][] = [

            [billToControl, 'billToControl'],
            [billingAddressLine1Control, 'addressLine1Control'],
            [billingAddressLine2Control, 'addressLine2Control'],
            [billingCityControl, 'cityControl'],
            [billingZipCodeControl, 'zipCodeControl'],
            [billingCountryIdControl, 'countryIdControl'],
            [billingStateIdControl, 'stateIdControl'],
            [billingPhoneControl, 'phoneControl'],
            [billingPhoneCountryIdControl, 'phoneCountryIdControl'],

            // https://wavesaudio.atlassian.net/browse/DSWEB-6
            [taxIdSelectedStatusControl, 'taxIdSelectedStatusControl']
        ];

        // https://wavesaudio.atlassian.net/browse/DSWEB-2247
        if(taxIdSelectedStatusControl.value === TaxIdStatusEnum.YES){
            billingFormFields.push([taxIdControl, 'taxIdControl']);
        }

        const isBillingFormValid = validateBillingAddress(billingFormFields, dispatch);

        if(!isBillingFormValid){
            isValid = false;
        }

        if(!isValid){
            // open 'the edit address form' if not opened yet
            sendCartEvent(ADDRESS_NOT_VALID_EVENT);
            return;
        }

        // validate shipping form -----------------------------
        if(cartData.isShippingNeeded){

            if(isShippingDifferentFromBilling){
                const shippingFormFields: [IFormControl, string][] = [

                    [shipToControl, 'shipToControl'],
                    [shippingAddressLine1Control, 'addressLine1Control'],
                    [shippingAddressLine2Control, 'addressLine2Control'],
                    [shippingCityControl, 'cityControl'],
                    [shippingZipCodeControl, 'zipCodeControl'],
                    [shippingCountryIdControl, 'countryIdControl'],
                    [shippingStateIdControl, 'stateIdControl'],
                    [shippingPhoneControl, 'phoneControl'],
                    [shippingPhoneCountryIdControl, 'phoneCountryIdControl'],

                    // https://wavesaudio.atlassian.net/browse/DSWEB-6
                    [taxIdControl, 'taxIdControl'],
                    [taxIdSelectedStatusControl, 'taxIdSelectedStatusControl']
                ];

                const isShippingFormValid = validateShippingAddress(shippingFormFields, dispatch);
                if(!isShippingFormValid){
                    isValid = false;
                }

                if(!isValid){
                    // open 'the edit address form' if not opened yet
                    sendCartEvent(ADDRESS_NOT_VALID_EVENT);
                    return;
                }
            }

            // validate ups shipping
            const isUPSShippingDropdownValid = validateShippingAddress([[upsShippingCodeControl, 'upsShippingCodeControl']], dispatch);
            if(!isUPSShippingDropdownValid){

                dispatch(
                    shippingAddressActions.main({
                        upsShippingError: 'Please select shipping option.',
                    })
                );

                // scroll to 'ups-shipping-code' section
                scrollToPath('#ups-shipping-code');
                return;
            }
        }

        // the main 'confirm order' entry point
        const paymentMethodToken = selectedPaymentTab === PaymentTabsEnum.CreditCard ? (
            selectedCreditCardTab === CreditCardTabTabsEnum.NewCreditCard ? newCreditCardToken : savedCreditCardToken
        ) : '';

        const isNewCreditCard = selectedPaymentTab === PaymentTabsEnum.CreditCard &&
            selectedCreditCardTab === CreditCardTabTabsEnum.NewCreditCard;

        const addressIDs = getAddressIDs();

        // don't pass a state id to the server, if the country doesn't have states (for example, old values from
        // session storage), because it may fail in validation.
        const billingSelectedCountry = countries.find(country => country.countryID.toString() === billingCountryIdControl.value.toString());
        const billingStateID = billingSelectedCountry &&
        billingSelectedCountry.states &&
        billingSelectedCountry.states.length > 0 ? Number(billingStateIdControl.value) || 0 : 0;

        // https://dev13.waves.com/cart?utm_source=aaa&utm_medium=bbb&utm_campaign=ddd
        const utm = getUTMFromStorage();

        const confirmOrderModel: IConfirmOrderRequest = {
            paymentMethodToken,
            networkTransactionID: isNewCreditCard ? '' : networkTransactionID,
            isRecurringNetworkTransactionID,
            cvv: cvvControl.value,
            paymentType: selectedPaymentTab,

            // new credit card parameters
            rememberMe,
            isNewCreditCard,

            // used for 'total changed' validation error
            checksumTotal: cartData.priceData.totalPrice,

            billingAddress: {
                addressID: addressIDs.billingAddressID,
                addressPersonalName: billToControl.value,
                addressLine1: billingAddressLine1Control.value,
                addressLine2: billingAddressLine2Control.value,
                addressCountryID: Number(billingCountryIdControl.value) || 0,
                addressStateID: billingStateID,
                addressCity: billingCityControl.value,
                addressZip: billingZipCodeControl.value,
                phoneNumber: billingPhoneControl.value,
                phoneCountryID: Number( billingPhoneCountryIdControl.value) || 0,

                taxID: isUSA ? '' : taxIdControl.value || '',
                taxIDSelectedStatus: isUSA ? TaxIdStatusEnum.NONE : (Number(taxIdSelectedStatusControl.value) || TaxIdStatusEnum.NONE),
            },

            isShippingDifferentFromBilling: cartData.isShippingNeeded && isShippingDifferentFromBilling,

            lastUTMSource: utm ? utm.lastSource : '',
            lastUTMMedium: utm ? utm.lastMedium : '',
            lastUTMCampaign: utm ? utm.lastCampaign : '',

            useCreditRecurring: isReferRecurringChecked,
        };

        if(cartData.isShippingNeeded && isShippingDifferentFromBilling){

            // don't pass a state id to the server, if the country doesn't have states (for example, old values from
            // session storage), because it may fail in validation.
            const shippingSelectedCountry = countries.find(country => country.countryID.toString() === shippingCountryIdControl.value.toString());
            const shippingStateID = shippingSelectedCountry &&
            shippingSelectedCountry.states &&
            shippingSelectedCountry.states.length > 0 ? Number(shippingStateIdControl.value) || 0 : 0;

            confirmOrderModel.shippingAddress = {
                addressID: addressIDs.shippingAddressID,
                addressPersonalName: shipToControl.value,
                addressLine1: shippingAddressLine1Control.value,
                addressLine2: shippingAddressLine2Control.value,
                addressCountryID: Number(shippingCountryIdControl.value) || 0,
                addressStateID: shippingStateID,
                addressCity: shippingCityControl.value,
                addressZip: shippingZipCodeControl.value,
                phoneNumber: shippingPhoneControl.value,
                phoneCountryID: Number( shippingPhoneCountryIdControl.value) || 0,
            };
        }

        if(cartData.isShippingNeeded){
            confirmOrderModel.upsShippingCode = upsShippingCodeControl.value;
        }

        setLoading(true);
        setServerError('');

        const response = await confirmOrder(confirmOrderModel);

        // server returns different status codes;
        // each status code or error code may cause its own behaviour
        const hasRedirect = handleConfirmOrderResponse(
            response,
            setServerError,
            (updatedCartData: ICartData) => {
                dispatch(
                    rootActions.setCartData(updatedCartData)
                );
            },
            isInAppPurchase,
            dispatch
        );

        if(!hasRedirect){
            setLoading(false);
        }
    };

    return (
        <div className="bg-white border rounded w-full md:w-5/12 lg:w-1/4 md:top-60px sticky self-start">
            <div className="m-6">

                <div className="flex items-center justify-between font-bold mb-4">
                    <p className="text-2xl text-gray-700 mr-2">Order Summary</p>

                    <button
                        type="button"
                        onClick={ editCartHandler }
                        data-qa="cart-step3-edit-cart"
                        className="text-blue-200 font-bold text-right">
                        Edit Cart
                    </button>
                </div>

                {
                    cartData && cartData.cartItems && cartData.cartItems
                        .filter(cartItem => cartItem.isActive)
                        .map((cartItem, i) => {

                            return (
                                <StepOverviewItem
                                    key={ cartItem.cartItemGUID }
                                    cartItem={ cartItem }
                                />
                            )
                        })
                }

                <SubscriptionOverviewItem />
                { cartData.subscription && <div className="border-b mb-4"></div> }

                <FreeWithYourPurchaseOverview />

                {
                    priceData.subTotalPrice !== priceData.totalPrice &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Subtotal</span>
                        <span className="ml-2" data-qa="cart-step3-subtotal"><ProductPriceFormat price={ priceData.subTotalPrice } /></span>
                    </div>
                }

                {
                    priceData.shipping > 0 &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Shipping</span>
                        <span className="ml-2" data-qa="cart-step3-ship-value"><ProductPriceFormat price={ priceData.shipping } /></span>
                    </div>
                }

                {
                    priceData.taxWithoutShippingTax > 0 &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Tax</span>
                        <span className="ml-2" data-qa="cart-step3-tax-value"><ProductPriceFormat price={ priceData.taxWithoutShippingTax } /></span>
                    </div>
                }

                {
                    priceData.shippingTax > 0 &&
                    <div className="text-gray-500 font-bold flex items-center justify-between mb-2">
                        <span className="mr-2">Shipping Tax</span>
                        <span className="ml-2" data-qa="cart-step3-ship-tax-value"><ProductPriceFormat price={ priceData.shippingTax } /></span>
                    </div>
                }

                {
                    priceData.totalDiscountPrice > 0 &&
                    <div className="text-green font-bold flex items-center justify-between mb-4">
                        <span className="mr-2">Discounts</span>
                        <span className="ml-2" data-qa="cart-step3-discounts-price">-<ProductPriceFormat price={ priceData.totalDiscountPrice } /></span>
                    </div>
                }

                <div className="text-3xl text-gray-700 flex items-center justify-between mb-4">
                    <span className="mr-2">Order Total</span>
                    <span className="ml-2" data-qa="cart-step3-total"><ProductPriceFormat price={ priceData.totalPrice } /></span>
                </div>

                <Coupon />
                <InstantSavings />
                { isConfirmationMsgVisible(cartData) && <FreePluginBox /> }

                <ReferCredit />
                <ReferCreditRecurring />

                <ConfirmText />

                {
                    !loading &&
                    <button
                        type="button"
                        className="w-btn text-xl font-normal py-3"
                        data-qa="cart-step3-confirm-order"
                        onClick={ proceedHandler }>
                        { isSubscription && <span>Agree and Subscribe</span> }
                        { !isSubscription && <span>Confirm Order</span> }
                    </button>
                }
                {
                    loading &&
                    <div className="flex justify-center my-6" data-qa="cart-step3-order-process-preloader">
                        <Preloader width="30px" height="30px" color="#4f4f4f" />
                    </div>
                }

                {
                    serverError && <div
                        className="error-box my-4"
                        data-qa="cart-step3-confirm-order-error"
                        dangerouslySetInnerHTML={{ __html: serverError }} />
                }
            </div>
        </div>
    )
};

export default Step3Overview;
